import { inject } from 'vue';
import { useAppStore, useUserStore, useActionStore } from '@coreStores/index';
import { useElementStore } from '@stores/element';
import { useGeneral, useWatch } from '@app/base-package';
import type { IKeyAny, IKeyObject, IKeyString, IImages, ISelectOption } from '@coreInterfaces/interface';
import { userTypeMap, type UserTypeKey } from '@composable/useConstant';
import Swal from 'sweetalert2';

/* ------ Declaration ------ */
const { onPopulateSelectOption, onRemoveQueryParams, setFormData } = useGeneral;
const { waitForBool } = useWatch;

/* ------ Methods ------ */
/**
 * Return URL by 2fa action.
 */
const get2FaUrl = (action: string): string => {
    const userStore = useUserStore();
    const actionTypes: IKeyObject<IKeyString> = {
        check: {
            adm: 'adminCheck2FA',
            stg: 'groupAdminCheck2FA',
            afl: 'affiliateUserCheck2FA'
        },
        verify: {
            adm: 'adminVerify2FA',
            stg: 'groupAdminVerify2FA',
            afl: 'affiliateUserVerify2FA'
        }
    };

    if (actionTypes[action]) {
        return actionTypes[action][userStore.getType()];
    }

    return '';
};

/**
 * Return api select options.
 *
 * @param {UserTypeKey}  key
 */
const getApiSelectOpt = async (key: UserTypeKey, onDirectRequest?: Function): Promise<void> => {
    const userStore = useUserStore();
    const actionStore = useActionStore();
    const elementStore = useElementStore();
    const userKey = userTypeMap[key];
    const actionKey = `${userKey}SelOpts`;

    onDirectRequest = onDirectRequest || ((await inject('onDirectRequest')) as Function);

    const apiSelOptions = await onDirectRequest(
        {},
        {
            action: actionKey,
            method: 'get',
            stores: { user: userStore, action: actionStore }
        }
    );

    if (apiSelOptions && apiSelOptions.data) {
        for (const dkey in apiSelOptions.data) {
            if (apiSelOptions.data[dkey].length) {
                const colKey = Object.keys(apiSelOptions.data[dkey][0]);
                elementStore.setSelectOpt(dkey, onPopulateSelectOption(apiSelOptions.data[dkey], colKey));
            } else {
                elementStore.setSelectOpt(dkey, []);
            }
        }
    }

    actionStore.setSelOptLoaded(true);
};

/**
 * Returns the app logo images.
 */
const getAppImages = (): IImages => {
    return {
        iconLight: {
            src: '../images/logo/icon-light.png?v1',
            alt: 'icon'
        },
        logoLight: {
            src: '../images/logo/logo-light.png?v3',
            alt: 'logo'
        }
    };
};

/**
 * This method retrieves the login cookie name by user type.
 *
 * @param {String}  userType
 * @param {Object}  options
 */
const getCookieName = (userType: string, options?: IKeyAny): string | string[] => {
    const cookiesName: IKeyString = {
        token: `aff_${userType}_token`,
        error: 'error',
        f_login: `${userType}_f_login`,
        fa_challenge_id: `${userType}_fa_challenge_id`,
        fa_expires: `${userType}_fa_expires`,
        fa_status: `${userType}_fa_status`,
        fa_state: `${userType}_fa_state`,
        new_login: `${userType}_new_login`,
        tname: 'tname',
        type: 'type'
    };

    if (options) {
        if ('isAll' in options) {
            return Object.values(cookiesName);
        }

        if ('cType' in options) {
            return cookiesName[options.cType];
        }
    }

    return cookiesName['token'];
};

/**
 * This method retrieves the login action.
 *
 * @param {String}  userType
 */
const getLoginUrl = (userType: string): string | string[] => {
    const loginAction: IKeyString = {
        adm: 'login',
        stg: 'groupLogin',
        afl: 'affiliateLogin'
    };

    return loginAction[userType] ?? Object.values(loginAction);
};

/**
 * This method retrieves the logout action.
 *
 * @param {String}  userType
 */
const getLogoutUrl = (userType: string): string => {
    const logoutAction: IKeyString = {
        adm: 'logout',
        stg: 'groupLogout',
        afl: 'affiliateLogout'
    };

    return logoutAction[userType];
};

/**
 * This method retrieves the me action.
 *
 * @param {String}  userType
 */
const getMeUrl = (userType: string): string => {
    const meAction: IKeyString = {
        adm: 'adminMe',
        stg: 'groupMe',
        afl: 'affiliateMe'
    };

    return meAction[userType] ?? '';
};

/**
 * This method retrieves the profile page name.
 *
 * @param {String}  userType
 */
const getProfilePage = (userType: string): string => {
    const profilePages: IKeyString = {
        adm: 'AdminMyProfile',
        stg: 'SiteGroupMyProfile',
        afl: 'AffiliateMyProfile'
    };

    return profilePages[userType];
};

/**
 * Retrieve select options from element store.
 *
 * @param {String}        key
 * @param {Array<string>} colKey
 *
 * @returns {String}
 */
const getSelectOptions = async (
    key: string,
    colKey: Array<string> = [],
    params: IKeyAny = {},
    isForceChange?: boolean,
    withAllOption?: boolean,
    onDirectRequest?: Function
): Promise<ISelectOption[] | any[]> => {
    const appStore = useAppStore();
    const userStore = useUserStore();
    const actionStore = useActionStore();

    await waitForBool(() => actionStore.getSelOptLoaded());

    const elementStore = useElementStore();
    let selOptions = elementStore.getSelectOptByKey(key);

    if (selOptions != null && !isForceChange) {
        if (withAllOption) {
            selOptions = [{ id: '-1', code: '', name: appStore.getText('all') }, ...selOptions];
        }

        const preOrder = ['Yes', 'No'];
        const allStatement = ['', 'All', 'ALL'];
        const sorted = selOptions.sort(function (a: ISelectOption, b: ISelectOption) {
            if (preOrder.includes(a['name']) && preOrder.includes(b['name'])) {
                return preOrder.indexOf(a.name) - preOrder.indexOf(b.name);
            } else if (allStatement.includes(a['code']) && allStatement.includes(b['code'])) {
                return a['name'].localeCompare(b['name']);
            } else {
                return 0;
            }
        });

        sorted.map(function (item: ISelectOption, idx: number) {
            if (['All', 'ALL'].includes(item.name)) {
                item.name = appStore.getText('all');
            }

            return item;
        });

        return sorted;
    }

    onDirectRequest = onDirectRequest || ((await inject('onDirectRequest')) as Function);

    const serverSelOptions = await onDirectRequest(params, {
        action: key,
        method: 'get',
        stores: { user: userStore, action: actionStore }
    });

    if (serverSelOptions && serverSelOptions.data.length) {
        elementStore.setSelectOpt(key, onPopulateSelectOption(serverSelOptions.data, colKey));
        let tmpOptons = elementStore.getSelectOptByKey(key);

        if (tmpOptons && withAllOption) {
            tmpOptons.unshift({ id: '-1', code: '', name: appStore.getText('all') });
        }

        return tmpOptons;
    }

    return [];
};

/**
 * Display download status and message.
 *
 * @param {Boolean}  isDone
 */
const onDataDownload = (isDone: boolean = false, excelData?: BlobPart): void => {
    if (isDone) {
        if (excelData) {
            /* Create a link element to trigger the download */
            const link = document.createElement('a');
            link.href = window.URL.createObjectURL(new Blob([excelData]));
            link.download = `members_${new Date().toISOString()}.xlsx`;
            link.click();
        }

        Swal.close();
        return;
    }

    const appStore = useAppStore();

    Swal.fire({
        title: `${appStore.getText('downloading')}...`,
        html: `
            <p id="progressText">${appStore.getText('downloading_msg')}</p>
            <div class="swal2-progress-bar">
                <div class="swal2-progress-bar-filled"></div>
            </div>
        `,
        icon: 'info',
        showConfirmButton: false,
        allowOutsideClick: false /* Prevent closing the popup */,
        didOpen: () => {
            // const progressText = document.getElementById('progressText');
            const progressBarFilled = document.querySelector('.swal2-progress-bar-filled') as HTMLElement;
            let progress = 0;
            // let ellipsis = '';

            /* Function to update progress and trigger the animation */
            const updateProgress = () => {
                if (!isDone) {
                    progress = (progress + 1) % 100; /* Increase progress by 1% (or you can adjust this) */
                    progressBarFilled.style.width = `${progress}%`;

                    /* Add animated ellipsis to the progress text */
                    // ellipsis = ellipsis.length === 3 ? '' : ellipsis + '.';
                    // progressText.textContent = `Please wait while the file is being downloaded${ellipsis}`;

                    /* Request the next animation frame for smooth updates */
                    requestAnimationFrame(updateProgress);
                }
            };

            /* Start the progress animation */
            updateProgress();
        }
    });
};

export {
    get2FaUrl,
    getApiSelectOpt,
    getAppImages,
    getCookieName,
    getLoginUrl,
    getLogoutUrl,
    getMeUrl,
    getProfilePage,
    getSelectOptions,
    onDataDownload,
    onRemoveQueryParams,
    setFormData
};
